import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bouton } from '../ui';
import { useTraduction } from '../i18n/langue';

/**
 * Le formulaire d'entrée, remonté depuis le bas de l'écran sur mobile.
 *
 * Sur un téléphone, ce qui compte tient sous le pouce. Le titre, l'explication et
 * l'illustration occupent le premier écran ; le formulaire arrive à la demande, en
 * feuille qui monte du bas — là où la main se trouve déjà.
 *
 * Bâti sur `<dialog>` et `showModal()`, et non sur un `div` positionné : l'élément natif
 * apporte le piège de focus, la fermeture par Échap, l'inertie de l'arrière-plan et le
 * voile. Les reconstituer à la main revient à réécrire ce que le navigateur fait déjà —
 * généralement moins bien, et au détriment des parents qui naviguent au clavier ou au
 * lecteur d'écran.
 */
export function FeuilleFormulaire({
  libelle,
  children,
  ouverteDEmblee = false,
}: {
  /** Libellé du bouton d'appel — « Se connecter » ou « Créer un compte ». */
  libelle: string;
  children: ReactNode;
  /**
   * Ouverte dès l'arrivée — pour la connexion seulement.
   *
   * Qui revient se connecter vient faire une chose : une frappe de plus avant d'atteindre
   * le premier champ est une frappe de trop, tous les jours. À l'inscription, l'écran doit
   * d'abord expliquer, et la feuille reste à la demande.
   */
  ouverteDEmblee?: boolean;
}) {
  const { messages } = useTraduction();
  const feuille = useRef<HTMLDialogElement>(null);
  const [ouverte, setOuverte] = useState(ouverteDEmblee);

  useEffect(() => {
    const element = feuille.current;
    if (!element) return;

    if (ouverte && !element.open) {
      // `showModal` peut ne pas exister (jsdom) : la feuille reste alors un bloc ordinaire
      // plutôt que de faire échouer le rendu.
      element.showModal?.();
    } else if (!ouverte && element.open) {
      element.close();
    }
  }, [ouverte]);

  return (
    <>
      <div className="appel-formulaire">
        <Bouton onClick={() => setOuverte(true)}>{libelle}</Bouton>
      </div>

      <dialog
        className="feuille-bas"
        ref={feuille}
        aria-label={libelle}
        // Échap et le clic sur le voile passent par `close`, jamais par un état qu'on
        // devrait tenir synchronisé à la main.
        onClose={() => setOuverte(false)}
        onClick={(evenement) => {
          // Un clic qui atteint le `dialog` lui-même vient du voile : le contenu est un
          // enfant, et arrête l'événement avant.
          if (evenement.target === feuille.current) setOuverte(false);
        }}
      >
        <div className="feuille-bas-contenu">
          <div className="feuille-bas-poignee" aria-hidden="true" />
          {children}
          <Bouton variante="secondaire" className="feuille-bas-fermer" onClick={() => setOuverte(false)}>
            {messages.entree.feuille.fermer}
          </Bouton>
        </div>
      </dialog>
    </>
  );
}
